import { useState, useMemo, useEffect, useCallback, Dispatch, SetStateAction } from 'react'
import { v4 as makeUuid } from 'uuid'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import {
  AvailableSettings,
  DEFAULT_SETTINGS_OBJECT,
  EmailCategories,
  SettingActionValue,
  SettingValue,
  SettingsObject,
  emailCategories
} from 'types/Settings'
import { useDomainPolicyAccess } from 'lib/hooks/useDomainPolicyAccess'

import {
  getAccountLightweightSettings,
  getDomainSettings,
  resetAccountAndDomainSettings,
  updateDomainSettings,
  updateAccountSettings
} from 'redux/features/settings/settingsSlice'
import { isSuccess } from 'redux/toolkit/api'
import { filterSettings } from 'components/libs/settings/settings'
import { useDirtyFormObjectCheck } from 'lib/useDirtyFormObjectCheck'
import { useInboundSettingsRights } from 'components/libs/userRights/pages/useInboundSettingsRights'
import { getBlockTransition, setBlockTransition } from 'lib/routes'
import { setErrorSnackBar } from 'redux/features/app/appSlice'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

interface FastspamScores {
  block: number
  quarantine: number
}

const clampScore = (value: number): number => {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(10, value))
}

const convertObjectValuesToInteger = (obj: Record<string, any>): FastspamScores => {
  const result = Object.keys(obj).reduce((acc, setting) => {
    acc[setting] = clampScore(Number(obj[setting]) || 0)
    return acc
  }, {} as Record<string, number>)
  return {
    block: result.block ?? 0,
    quarantine: result.quarantine ?? 0
  }
}

export interface State {
  pdDomainId: boolean
  canEmailCategorization: boolean
  canQuarantineSettings: boolean
  canSendBrtsEvidence: boolean
  settings: SettingsObject
  key: string
  tableData: {
    total: number
    data: EmailCategories[]
  }
  isDirtyForm: boolean
  fastSpamScore: FastspamScores
  isBulkExemptionsOn: boolean
  isAdditionalExemptionsOn: boolean
  isBrtsExemptionsOn: boolean
  isBrblExemptionsOn: boolean
  hasNoDomainPolicyAccess: boolean
}

export interface EventHandlers {
  onFormChange: (name: AvailableSettings) => (NewValue: any) => void
  onScoringChange: (name: ScoringType, value: number) => void
  onActionChange: (e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => void
  onSave: () => void
  onCancelConfirm: () => void
  helpConfig: {
    isOpen: boolean
    onHelpClick: () => void
    onCloseHelp: () => void
  }
  setIsBulkEmailExemptTableDirty: Dispatch<SetStateAction<boolean>>
  setIsBrtsExemptTableDirty: Dispatch<SetStateAction<boolean>>
  setIsBrblExemptTableDirty: Dispatch<SetStateAction<boolean>>
}

export type UseReputationLogic = [State, EventHandlers]

export enum ScoringType {
  QUARANTINE = 'quarantine',
  BLOCK = 'block'
}

export const useReputationLogic = (): UseReputationLogic => {
  const SETTINGS_LIST = useMemo(
    () => [
      AvailableSettings.BRBL,
      AvailableSettings.VSCAN,
      AvailableSettings.BRTS,
      AvailableSettings.FASTSPAM_ENABLED,
      AvailableSettings.FASTSPAM_SCORES,
      AvailableSettings.BULK_EMAIL_DETECTION
    ],
    []
  )
  const dispatch = useAppDispatch()
  const [formObject, setFormObject] = useState<SettingsObject>(DEFAULT_SETTINGS_OBJECT)
  const [isHelpDialogOpened, setIsHelpDialogOpened] = useState<boolean>(false)
  const { canQuarantineSettings, canEmailCategorization, canSendBrtsEvidence } = useInboundSettingsRights()
  // Bulk Email exemptions use the older feature flag
  const isBulkExemptionsOn = isMyFeatureOn(FEATURES.EGD_React_Bulk_Exemptions)
  // Additional exemptions (BRTS, BRBL) use the newer feature flag
  const isAdditionalExemptionsOn = isMyFeatureOn(FEATURES.EGD_React_Additional_Exemptions)

  const {
    accessTokenObject,
    isUpdateAccountSettingsSuccess,
    isUpdateDomainSettingsSuccess,
    accountSettings,
    domainSettings
  } = useAppSelector(_stores => ({
    accessTokenObject: _stores.auth.accessTokenObject,
    isUpdateAccountSettingsSuccess: isSuccess(_stores.settings.updateAccountSettingsApiStatus),
    isUpdateDomainSettingsSuccess: isSuccess(_stores.settings.updateDomainSettingsApiStatus),
    accountSettings: _stores.settings.accountSettings,
    domainSettings: _stores.settings.domainSettings
  }))

  // Domain policy access check - only applies in domain-manage mode (when pdDomainId is set)
  const pdDomainIdValue = accessTokenObject?.pdDomainId
  const isInDomainManageMode = !!pdDomainIdValue
  const { hasNoDomainPolicyAccess: rawHasNoDomainPolicyAccess } = useDomainPolicyAccess(Number(pdDomainIdValue) || 0)
  // Only block access when in domain-manage mode AND user has no R or U access
  const hasNoDomainPolicyAccess = isInDomainManageMode && rawHasNoDomainPolicyAccess

  const [{ isDirty, changedSettings }, resetInitialForm] = useDirtyFormObjectCheck(formObject)
  const [isBulkEmailExemptTableDirty, setIsBulkEmailExemptTableDirty] = useState<boolean>(false)
  const [isBrtsExemptTableDirty, setIsBrtsExemptTableDirty] = useState<boolean>(false)
  const [isBrblExemptTableDirty, setIsBrblExemptTableDirty] = useState<boolean>(false)

  // key is used to force a re-render of <SettingsPage>
  // The Cancel button is supposed to reset the initial form values only after a confirmation.
  // The need for a confirmation (block) is set up via useDirtyFormCheck
  const [key, setKey] = useState(makeUuid())

  // init
  useEffect(() => {
    if (hasNoDomainPolicyAccess) {
      return
    }
    if (canSendBrtsEvidence) {
      SETTINGS_LIST.push(AvailableSettings.BRTS_SEND_EVIDENCE)
    }
    if (canEmailCategorization) {
      SETTINGS_LIST.push(
        AvailableSettings.EMAILCAT_CORPORATE,
        AvailableSettings.EMAILCAT_LISTSERVER,
        AvailableSettings.EMAILCAT_MARKETING,
        AvailableSettings.EMAILCAT_SOCIALMEDIA,
        AvailableSettings.EMAILCAT_TRANSACTIONAL
      )
    }
    if (accessTokenObject?.pdDomainId) {
      dispatch(getDomainSettings({ domainId: accessTokenObject?.pdDomainId, settings: SETTINGS_LIST }))
    } else {
      const exemptionSettings: AvailableSettings[] = []
      // Bulk Email exemptions use older feature flag
      if (isBulkExemptionsOn) {
        exemptionSettings.push(AvailableSettings.BULK_EMAIL_SENDER_EXEMPT, AvailableSettings.BULK_EMAIL_RCPT_EXEMPT)
      }
      // BRTS/BRBL exemptions use newer feature flag
      if (isAdditionalExemptionsOn) {
        exemptionSettings.push(AvailableSettings.BRTS_EXEMPT, AvailableSettings.BRBL_EXEMPT)
      }
      dispatch(getAccountLightweightSettings([...SETTINGS_LIST, ...exemptionSettings]))
    }
    // eslint-disable-next-line
  }, [canEmailCategorization, hasNoDomainPolicyAccess])

  // unmount
  useEffect(
    () => () => {
      dispatch(resetAccountAndDomainSettings())
    },
    [dispatch]
  )

  const fastSpamScore = useMemo(() => {
    const fastSpamScores = formObject.fastspam_scores
    if (typeof fastSpamScores === 'string') {
      try {
        return convertObjectValuesToInteger(JSON.parse(fastSpamScores))
      } catch (error) {
        dispatch(
          setErrorSnackBar({
            message: 'error_parsing_settings'
          })
        )
        return { block: 0, quarantine: 0 }
      }
    } else if (fastSpamScores && typeof fastSpamScores === 'object') {
      return convertObjectValuesToInteger(fastSpamScores)
    } else {
      return { block: 0, quarantine: 0 }
    }
  }, [formObject.fastspam_scores, dispatch])

  const resetForm = useCallback(
    (settings: SettingsObject) => {
      const filteredSettings = filterSettings(settings, SETTINGS_LIST)
      const settingsWithDefaults = setValueWithDefaults(filteredSettings)

      setFormObject(settingsWithDefaults)
      resetInitialForm(settingsWithDefaults)
    },
    [resetInitialForm, SETTINGS_LIST]
  )

  useEffect(() => {
    if (accessTokenObject?.pdDomainId) {
      resetForm(domainSettings)
    } else {
      resetForm(accountSettings)
    }
  }, [resetForm, accountSettings, domainSettings, accessTokenObject])

  const onHelpClick = useCallback(() => {
    setIsHelpDialogOpened(true)
  }, [])

  const onCloseHelp = useCallback(() => {
    setIsHelpDialogOpened(false)
  }, [])

  const setValueWithDefaults = (settings: SettingsObject) => {
    const settingsWithDefaults = settings

    if (!settingsWithDefaults.brbl) {
      settingsWithDefaults[AvailableSettings.BRBL] = SettingActionValue.QUARANTINE
    }

    if (!settingsWithDefaults.vscan) {
      settingsWithDefaults[AvailableSettings.VSCAN] = SettingValue.ENABLED
    }

    if (!settingsWithDefaults.brts) {
      settingsWithDefaults[AvailableSettings.BRTS] = SettingActionValue.QUARANTINE
    }

    if (!settingsWithDefaults.fastspam_enabled) {
      settingsWithDefaults[AvailableSettings.FASTSPAM_ENABLED] = '1'
    }

    if (!settingsWithDefaults.fastspam_scores) {
      settingsWithDefaults[AvailableSettings.FASTSPAM_SCORES] = '{"block": 5, "quarantine": 0}'
    }

    emailCategories.forEach(cat => {
      const catKey = cat.variable
      const defaultValue = cat.default
      if (!settingsWithDefaults[catKey]) {
        settingsWithDefaults[catKey] = defaultValue
      }
    })

    return settingsWithDefaults
  }

  useEffect(() => {
    if (isUpdateAccountSettingsSuccess || isUpdateDomainSettingsSuccess) {
      if (accessTokenObject?.pdDomainId) {
        dispatch(getDomainSettings({ domainId: accessTokenObject?.pdDomainId, settings: SETTINGS_LIST }))
      } else {
        const exemptionSettings: AvailableSettings[] = []
        // Bulk Email exemptions use older feature flag
        if (isBulkExemptionsOn) {
          exemptionSettings.push(AvailableSettings.BULK_EMAIL_SENDER_EXEMPT, AvailableSettings.BULK_EMAIL_RCPT_EXEMPT)
        }
        // BRTS/BRBL exemptions use newer feature flag
        if (isAdditionalExemptionsOn) {
          exemptionSettings.push(AvailableSettings.BRTS_EXEMPT, AvailableSettings.BRBL_EXEMPT)
        }
        dispatch(getAccountLightweightSettings([...SETTINGS_LIST, ...exemptionSettings]))
      }
    }
  }, [
    dispatch,
    isBulkExemptionsOn,
    isAdditionalExemptionsOn,
    accessTokenObject,
    isUpdateAccountSettingsSuccess,
    isUpdateDomainSettingsSuccess,
    SETTINGS_LIST
  ])

  const onFormChange = useCallback(
    (name: AvailableSettings) => (newValue: any) => {
      if (name === AvailableSettings.BRTS_SEND_EVIDENCE) {
        const dbValue = formObject.brts_send_evidence === '1' ? '0' : '1'
        setFormObject({ ...formObject, [name]: dbValue } as SettingsObject)
      } else {
        setFormObject({ ...formObject, [name]: newValue } as SettingsObject)
      }
    },
    [formObject]
  )

  const onScoringChange = useCallback(
    (name: ScoringType, value: number) => {
      try {
        let fastspamScores = formObject.fastspam_scores
        if (typeof fastspamScores === 'string') {
          fastspamScores = JSON.parse(fastspamScores)
        }
        if (!fastspamScores || typeof fastspamScores !== 'object') {
          throw new Error(
            `Invalid fastspamScores: expected object, got ${fastspamScores === null ? 'null' : typeof fastspamScores}`
          )
        }
        // Clone to avoid mutating frozen/readonly objects
        const scores = { ...fastspamScores }
        // Clamp value to valid range 0-10
        const clampedValue = clampScore(value)
        if (name === ScoringType.QUARANTINE && clampedValue >= scores.block) {
          scores.quarantine = scores.block - 1 < 0 ? 0 : scores.block - 1
        } else if (name === ScoringType.BLOCK && clampedValue <= scores.quarantine) {
          scores.block = clampedValue
          scores.quarantine = Math.max(clampedValue - 1, 0)
        } else {
          scores[name] = clampedValue
        }

        setFormObject({
          ...formObject,
          [AvailableSettings.FASTSPAM_SCORES]: JSON.stringify(scores)
        })
      } catch (error) {
        dispatch(
          setErrorSnackBar({
            message: 'error_parsing_settings'
          })
        )
      }
    },
    [formObject, dispatch]
  )

  const onActionChange = useCallback(
    (e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      const { name, value } = e.target
      setFormObject({ ...formObject, [name as string]: value })
    },
    [formObject]
  )

  const onSave = useCallback(() => {
    const updatedSettings = { ...changedSettings }
    if (updatedSettings[AvailableSettings.FASTSPAM_SCORES]) {
      try {
        const currentFastspamScores = convertObjectValuesToInteger(
          JSON.parse(updatedSettings[AvailableSettings.FASTSPAM_SCORES] as string)
        )

        const transformedFastspamScores = {
          block: currentFastspamScores.block,
          quarantine: currentFastspamScores.quarantine
        }
        updatedSettings[AvailableSettings.FASTSPAM_SCORES] = transformedFastspamScores
      } catch (error) {
        dispatch(
          setErrorSnackBar({
            message: 'error_parsing_settings'
          })
        )
      }
    }
    if (accessTokenObject?.pdDomainId) {
      dispatch(
        updateDomainSettings({
          domainId: accessTokenObject.pdDomainId,
          settings: updatedSettings
        })
      )
    } else {
      dispatch(updateAccountSettings({ settings: updatedSettings }))
    }
    // Reset exemption table dirty states after save
    setIsBulkEmailExemptTableDirty(false)
    setIsBrtsExemptTableDirty(false)
    setIsBrblExemptTableDirty(false)
  }, [dispatch, changedSettings, accessTokenObject])

  const onCancelConfirm = useCallback(() => {
    const block = getBlockTransition()
    if (block) {
      block(() => {
        if (accessTokenObject?.pdDomainId) {
          resetForm(domainSettings)
        } else {
          resetForm(accountSettings)
        }
        setBlockTransition(undefined)
        setKey(makeUuid())
      })
    }
  }, [resetForm, accessTokenObject, domainSettings, accountSettings])

  return useMemo(
    () => [
      {
        pdDomainId: !!accessTokenObject?.pdDomainId,
        canQuarantineSettings,
        canEmailCategorization,
        canSendBrtsEvidence,
        settings: formObject,
        tableData: {
          total: emailCategories.length,
          data: emailCategories
        },
        isDirtyForm: isDirty || isBulkEmailExemptTableDirty || isBrtsExemptTableDirty || isBrblExemptTableDirty,
        fastSpamScore,
        key,
        isBulkExemptionsOn, // Bulk Email exemptions use older feature flag (EGD_React_Bulk_Exemptions)
        isAdditionalExemptionsOn, // Additional exemptions (BRTS, BRBL) use newer feature flag
        isBrtsExemptionsOn: !!isAdditionalExemptionsOn && formObject.brts !== '0',
        isBrblExemptionsOn: !!isAdditionalExemptionsOn && formObject.brbl !== '0',
        hasNoDomainPolicyAccess
      },
      {
        onFormChange,
        onScoringChange,
        onActionChange,
        onSave,
        onCancelConfirm,
        helpConfig: {
          isOpen: isHelpDialogOpened,
          onHelpClick,
          onCloseHelp
        },
        setIsBulkEmailExemptTableDirty,
        setIsBrtsExemptTableDirty,
        setIsBrblExemptTableDirty
      }
    ],
    [
      accessTokenObject,
      canQuarantineSettings,
      canEmailCategorization,
      canSendBrtsEvidence,
      formObject,
      isDirty,
      key,
      isBulkEmailExemptTableDirty,
      isBrtsExemptTableDirty,
      isBrblExemptTableDirty,
      isBulkExemptionsOn,
      isAdditionalExemptionsOn,
      onFormChange,
      onScoringChange,
      onActionChange,
      onSave,
      onCancelConfirm,
      isHelpDialogOpened,
      onHelpClick,
      onCloseHelp,
      fastSpamScore,
      setIsBulkEmailExemptTableDirty,
      setIsBrtsExemptTableDirty,
      setIsBrblExemptTableDirty,
      hasNoDomainPolicyAccess
    ]
  )
}
